<template>
  <PopupShell
    :show="Boolean(popupComponent && popupViewModel)"
    :escapePriority="1000"
    :maxWidth="popupMaxWidth"
    :forceFullscreen="isWordView"
    :disableClose="isPaywall"
    :hideCloseButton="isPaywall || isMnemonic"
    @close="closeCurrentPopup"
  >
    <component
      v-if="popupComponent && popupViewModel"
      :is="popupComponent"
      :viewModel="popupViewModel"
    />
  </PopupShell>
</template>

<script setup lang="ts">
import { computed, type Component } from 'vue';
import { MnemonicPopupViewModel, QuizPopupViewModel } from '@lingvo-news/contracts';
import { getVueEnv } from '../../infrastructure/vue-env';
import PopupShell from './PopupShell.vue';
import QuizPopup from '../quizz/QuizPopup.vue';
import WordViewPopup from '../quizz/WordViewPopup.vue';
import BuyMeCoffeePopup from '../pages/user-categories/components/BuyMeCoffeePopup.vue';
import InfoPopup from './InfoPopup.vue';
import PaywallPopup from './PaywallPopup.vue';
import MnemonicPopup from './MnemonicPopup.vue';
import ReferralInvitePopup from './ReferralInvitePopup.vue';
import type { BuyMeCoffeePopupViewModel } from '../popup-view-models/buyme-coffee-popup.view-model';
import type { InfoPopupViewModel } from '../popup-view-models/info-popup.view-model';
import type { ReferralInvitePopupViewModel } from '../popup-view-models/referral-invite-popup.view-model';
import type { WordViewPopupViewModel } from '../popup-view-models/word-view-popup.view-model';
import type { PaywallPopupViewModel } from '@lingvo-news/contracts';

const env = getVueEnv();
const popupService = env.popupService;

const currentPopup = computed(() => popupService.currentPopup);

const isPaywall = computed<boolean>(() => currentPopup.value?.popupId === 'paywall');
const isWordView = computed<boolean>(() => currentPopup.value?.popupId === 'word-view');
const isMnemonic = computed<boolean>(() => currentPopup.value?.popupId === 'mnemonic');

const popupMaxWidth = computed<number>(() => {
  const popup = currentPopup.value;
  if (!popup) return 900;

  switch (popup.popupId) {
    case 'buyme-coffee':
      return 560;
    case 'info':
      return 720;
    case 'paywall':
      return 520;
    case 'referral-invite':
      return 620;
    case 'quiz':
      return 900;
    case 'mnemonic':
      return 920;
    case 'word-view':
      return 900;
    default:
      return 900;
  }
});

interface CloseablePopupViewModel {
  handleClose(): void;
}

function isCloseablePopupViewModel(popup: unknown): popup is CloseablePopupViewModel {
  if (!popup || typeof popup !== 'object') return false;
  return typeof (popup as { readonly handleClose?: unknown }).handleClose === 'function';
}

function closeCurrentPopup(): void {
  const popup = currentPopup.value;
  if (!popup) return;
  if (popup.popupId === 'paywall') return;
  if (isCloseablePopupViewModel(popup)) {
    popup.handleClose();
  }
}

const popupComponent = computed<Component | null>(() => {
  const popup = currentPopup.value;
  if (!popup) return null;

  switch (popup.popupId) {
    case 'quiz':
      return QuizPopup;
    case 'word-view':
      return WordViewPopup;
    case 'buyme-coffee':
      return BuyMeCoffeePopup;
    case 'info':
      return InfoPopup;
    case 'paywall':
      return PaywallPopup;
    case 'referral-invite':
      return ReferralInvitePopup;
    case 'mnemonic':
      return MnemonicPopup;
    default:
      return null;
  }
});

const popupViewModel = computed(() => {
  const popup = currentPopup.value;
  if (!popup) return null;
  if (popup.popupId === 'quiz' && popup instanceof QuizPopupViewModel) {
    return popup;
  }
  if (popup.popupId === 'word-view') {
    return popup as unknown as WordViewPopupViewModel;
  }
  if (popup.popupId === 'buyme-coffee') {
    return popup as unknown as BuyMeCoffeePopupViewModel;
  }
  if (popup.popupId === 'info') {
    return popup as unknown as InfoPopupViewModel;
  }
  if (popup.popupId === 'paywall') {
    return popup as unknown as PaywallPopupViewModel;
  }
  if (popup.popupId === 'referral-invite') {
    return popup as unknown as ReferralInvitePopupViewModel;
  }
  if (popup.popupId === 'mnemonic' && popup instanceof MnemonicPopupViewModel) {
    return popup;
  }
  return null;
});
</script>


